import { createContext, useContext } from 'react';

export interface QueuedSearchCompanySync {
    queued?: number;
    statusUrl?: string;
    syncRequestId?: string;
}

export interface SearchCompanySyncRequest {
    expectedTotal: number;
    progress?: SearchCompanySyncRequestProgress;
    statusUrl: string;
    syncRequestId: string;
}

export interface SearchCompanySyncRequestProgress {
    completed: number;
    failed: number;
    status: 'queued' | 'running' | 'completed' | 'completed_with_errors';
    total: number;
}

export interface SearchCompanySyncRun {
    companyCount: number;
    id: string;
    requests: SearchCompanySyncRequest[];
}

export interface SearchCompanySyncSummary {
    companyCount: number;
    completedCompanyCount: number;
    percentage: number;
}

interface SearchCompanySyncProgressContextValue {
    progress: SearchCompanySyncSummary | null;
    trackQueuedSyncs: (syncs: QueuedSearchCompanySync[], companyCount: number) => void;
}

export const isTerminalSearchCompanySyncStatus = (status?: SearchCompanySyncRequestProgress['status']) =>
    status === 'completed' || status === 'completed_with_errors';

export const getSearchCompanySyncSummary = (runs: SearchCompanySyncRun[]): SearchCompanySyncSummary | null => {
    if (!runs.length) {
        return null;
    }

    const companyCount = runs.reduce((total, run) => total + run.companyCount, 0);
    const totalOperations = runs.reduce(
        (total, run) =>
            total +
            run.requests.reduce(
                (requestTotal, request) => requestTotal + (request.progress?.total ?? request.expectedTotal),
                0,
            ),
        0,
    );
    const completedOperations = runs.reduce(
        (total, run) =>
            total +
            run.requests.reduce(
                (requestTotal, request) =>
                    requestTotal + (request.progress?.completed ?? 0) + (request.progress?.failed ?? 0),
                0,
            ),
        0,
    );
    const progressRatio = totalOperations > 0 ? Math.min(completedOperations / totalOperations, 1) : 0;

    return {
        companyCount,
        completedCompanyCount:
            progressRatio >= 1 ? companyCount : Math.min(Math.floor(companyCount * progressRatio), companyCount),
        percentage: Math.min(Math.floor(progressRatio * 100), 100),
    };
};

export const SearchCompanySyncProgressContext = createContext<SearchCompanySyncProgressContextValue>({
    progress: null,
    trackQueuedSyncs: () => undefined,
});

export const useSearchCompanySyncProgress = () => useContext(SearchCompanySyncProgressContext);
